import { STATUS, INK, BRAND } from 'Utils/styleTokens';
import {
  deriveMaskStatusByDataSource, mergeEncryptionJobsForDataSource, mapEncryptionRowStatus,
} from 'Secure/api';

/**
 * The data source's remediation STATE, stored as the `state` data-source tag.
 *
 * This is deliberately built on the existing generic tag facility (`data_source_tags`,
 * `PUT /api/datasources/{id}/tags/{key}`) rather than a new column: no migration, and the value
 * rides along on every data-source DTO under `tags`, which is how the Enterprise landscape home
 * reads it without a second request.
 *
 * Five of the six values are DERIVED from what the platform already knows (see `deriveState`) and
 * are refreshed when someone opens the source's EDL detail page. The sixth, MANAGED, is an
 * explicit human decision — "I am handling this one, leave it alone" — so nothing derived ever
 * overwrites it; only the Actions → Set to Auto item hands the source back to derivation.
 */
export const SOURCE_STATE = {
  UNKNOWN: 'unknown',
  AT_RISK: 'at_risk',
  CLEAN: 'clean',
  MANAGED: 'managed',
  MASKED: 'masked',
  ENCRYPTED: 'encrypted',
};

/** The tag key. Must satisfy the server's key pattern: lowercase, starts with a letter. */
export const STATE_TAG_KEY = 'state';

export const STATE_LABELS = {
  [SOURCE_STATE.UNKNOWN]: 'Unknown',
  [SOURCE_STATE.AT_RISK]: 'At risk',
  [SOURCE_STATE.CLEAN]: 'Clean',
  [SOURCE_STATE.MANAGED]: 'Managed',
  [SOURCE_STATE.MASKED]: 'Masked',
  [SOURCE_STATE.ENCRYPTED]: 'Encrypted',
};

export const STATE_HELP = {
  [SOURCE_STATE.UNKNOWN]: 'Never scanned — nothing has been done with this source yet.',
  [SOURCE_STATE.AT_RISK]: 'Sensitive data was found and has not been masked or encrypted.',
  [SOURCE_STATE.CLEAN]: 'Scanned, and no sensitive data was found.',
  [SOURCE_STATE.MANAGED]: 'Managed outside C2: no automated remediation will be applied to it.',
  [SOURCE_STATE.MASKED]: 'This source has been masked.',
  [SOURCE_STATE.ENCRYPTED]: 'This source has been encrypted.',
};

/** Pill colours, same families the severity pills use so the two read as one row of tags. */
export const STATE_PILL = {
  [SOURCE_STATE.UNKNOWN]: { bg: INK[100], fg: INK[500] },
  [SOURCE_STATE.AT_RISK]: { bg: STATUS.danger.bg, fg: STATUS.danger.text },
  [SOURCE_STATE.CLEAN]: { bg: STATUS.success.bg, fg: STATUS.success.text },
  [SOURCE_STATE.MANAGED]: { bg: INK[100], fg: INK[700] },
  [SOURCE_STATE.MASKED]: { bg: 'var(--op-mask-bg)', fg: BRAND.violet },
  [SOURCE_STATE.ENCRYPTED]: { bg: STATUS.info.bg, fg: STATUS.info.text },
};

const VALUES = new Set(Object.values(SOURCE_STATE));

/** The stored state for a source, or null when it carries no (recognised) `state` tag. */
export function storedState(tags) {
  const raw = tags && typeof tags === 'object' ? String(tags[STATE_TAG_KEY] || '').toLowerCase() : '';
  return VALUES.has(raw) ? raw : null;
}

/** True when the user has taken this source out of automated remediation. */
export function isManaged(tags) {
  return storedState(tags) === SOURCE_STATE.MANAGED;
}

/**
 * The state a source is in, from what the platform knows about it.
 *
 * @param severity    the EDL summary's severity (CRITICAL … CLEAN, NEVER_SCANNED)
 * @param maskStatus  whole-source mask status from the masking jobs ('MASKED' | 'MASKING' | …)
 * @param encrypted   true when the source has a successful encrypt job and has not been decrypted
 *
 * Protection wins over risk: a masked source that still has findings recorded against its last
 * pre-mask run is MASKED, not AT RISK — the findings are what was masked.
 */
export function deriveState({ severity, maskStatus, encrypted } = {}) {
  if (encrypted) return SOURCE_STATE.ENCRYPTED;
  if (maskStatus === 'MASKED') return SOURCE_STATE.MASKED;
  if (severity === 'NEVER_SCANNED' || severity == null) return SOURCE_STATE.UNKNOWN;
  if (severity === 'CLEAN') return SOURCE_STATE.CLEAN;
  return SOURCE_STATE.AT_RISK;
}

/**
 * What to show for a source: its explicit MANAGED tag if it has one, otherwise the derived state.
 * Used by both the detail page's pill and the home grid's layout.
 */
export function effectiveState(tags, derivedInputs) {
  const stored = storedState(tags);
  if (stored === SOURCE_STATE.MANAGED) return SOURCE_STATE.MANAGED;
  return deriveState(derivedInputs);
}

/** The two protection states — the source has actually been secured, not merely scanned. */
export const PROTECTED_STATES = [SOURCE_STATE.MASKED, SOURCE_STATE.ENCRYPTED];

/** MASKED / ENCRYPTED if that is what the tag says, else null. Used as the offline fallback. */
export function protectionFromTags(tags) {
  const stored = storedState(tags);
  return PROTECTED_STATES.includes(stored) ? stored : null;
}

/**
 * Protection state for EVERY data source at once, from the three job lists.
 *
 * The landscape home needs this for the whole grid, so it cannot use `deriveState`'s per-source
 * shape (one `mergeEncryptionJobsForDataSource` call per source). Same job semantics though —
 * masked = latest non-cancelled masking job SUCCEEDED; encrypted = latest non-cancelled
 * encrypt/decrypt job is a SUCCEEDED encrypt — so the badge on a tile and the pill on that
 * source's detail page always agree.
 *
 * @returns {Object} data-source id (as a string) → SOURCE_STATE.MASKED | SOURCE_STATE.ENCRYPTED.
 *                   Sources with no protection are absent, not null-valued.
 */
export function deriveProtectionByDataSource(maskingJobs, encryptJobs, decryptJobs) {
  const out = {};
  const maskStatus = deriveMaskStatusByDataSource(maskingJobs || []);
  Object.keys(maskStatus).forEach((id) => {
    if (maskStatus[id] === 'MASKED') out[String(id)] = SOURCE_STATE.MASKED;
  });
  // Encryption is resolved per source because a decrypt job reverts an earlier encrypt, so the
  // two lists only mean something merged. Only ids that appear in them can be encrypted.
  const ids = new Set();
  [...(encryptJobs || []), ...(decryptJobs || [])].forEach((j) => {
    if (j && j.dataSourceId != null) ids.add(String(j.dataSourceId));
  });
  ids.forEach((id) => {
    const merged = mergeEncryptionJobsForDataSource(encryptJobs || [], decryptJobs || [], id);
    // Encryption wins over masking, as it does in deriveState.
    if (mapEncryptionRowStatus(merged) === 'ENCRYPTED') out[id] = SOURCE_STATE.ENCRYPTED;
  });
  return out;
}
